<template>
  <section class="panel pad">
    <div class="panel-head">
      <h2>资讯推送</h2>
      <div class="head-actions">
        <button type="button" class="btn ghost sm" @click="load">刷新</button>
        <button type="button" class="btn primary sm" @click="startCreate">写文章</button>
      </div>
    </div>
    <p class="muted">
      内容复制到本站，前台不挂公众号外链。支持链接抓取 / HTML 粘贴生成草稿，确认后再发布。
    </p>
    <p v-if="error" class="error">{{ error }}</p>
    <p v-if="message" class="ok">{{ message }}</p>

    <div class="mode-tabs">
      <button type="button" :class="{ on: mode === 'list' }" @click="mode = 'list'">文章列表</button>
      <button type="button" :class="{ on: mode === 'import' }" @click="mode = 'import'">链接导入</button>
      <button v-if="editing" type="button" :class="{ on: mode === 'edit' }" @click="mode = 'edit'">
        {{ draft.id ? `编辑 #${draft.id}` : '编辑中' }}
      </button>
    </div>

    <!-- 导入 -->
    <div v-if="mode === 'import'" class="sub-panel">
      <div class="form-grid">
        <label class="full">
          <span>文章链接</span>
          <input v-model="importUrl" placeholder="https://mp.weixin.qq.com/s/… 或其它文章页" @keyup.enter="doPreview" />
        </label>
        <label class="full">
          <span>或粘贴 HTML（抓取被拦时）</span>
          <textarea v-model="importHtml" rows="4" placeholder="粘贴整页或正文 HTML…" />
        </label>
      </div>
      <div class="actions">
        <button type="button" class="btn ghost sm" :disabled="importing" @click="doPreview">预览清洗结果</button>
        <button type="button" class="btn primary sm" :disabled="importing" @click="doImportOne">
          {{ importing ? '处理中…' : '导入为草稿' }}
        </button>
      </div>

      <div v-if="importPreview" class="import-preview">
        <div class="preview-head">
          <div>
            <h3>{{ importPreview.title }}</h3>
            <p class="muted">{{ importPreview.summary }}</p>
          </div>
          <span v-if="importPreview.is_wechat" class="pill">公众号</span>
        </div>
        <div class="preview-box" v-html="importPreview.body_html" />
        <div v-if="importPreview.suggested_cards?.length" class="picked">
          <span class="muted">建议关联：</span>
          <button
            v-for="card in importPreview.suggested_cards"
            :key="card.id"
            type="button"
            class="chip"
            @click="addSuggested(card)"
          >{{ card.name }} +</button>
        </div>
        <div class="actions">
          <button type="button" class="btn primary sm" @click="usePreviewIntoEditor">填入编辑器</button>
        </div>
      </div>

      <label class="block-label">批量导入（每行一条，最多 20）</label>
      <textarea v-model="batchUrls" rows="4" class="batch-box" placeholder="https://…&#10;https://…" />
      <div class="actions">
        <button type="button" class="btn ghost sm" :disabled="importing" @click="doBatch">批量导入草稿</button>
      </div>
      <ul v-if="batchResults.length" class="batch-results">
        <li v-for="(item, idx) in batchResults" :key="idx" :class="item.ok ? 'ok' : 'bad'">
          <template v-if="item.ok">
            ✓ #{{ item.article_id }} {{ item.title }}
            <button type="button" class="linkish" @click="openEdit(item.article_id)">编辑</button>
          </template>
          <template v-else>✗ {{ item.url }} — {{ item.error }}</template>
        </li>
      </ul>
    </div>

    <!-- 编辑 -->
    <div v-else-if="mode === 'edit' && editing" class="sub-panel">
      <div class="form-grid">
        <label><span>标题 *</span><input v-model="draft.title" placeholder="文章标题" /></label>
        <label><span>URL 别名</span><input v-model="draft.slug" placeholder="留空则按标题生成；发布后勿随意改" /></label>
        <label class="full"><span>摘要</span><textarea v-model="draft.summary" rows="2" placeholder="列表与 SEO 描述" /></label>
        <label><span>标签</span><input v-model="draft.tags" placeholder="逗号分隔，如 H100,国产" /></label>
        <label><span>SEO 关键词</span><input v-model="draft.seo_keywords" placeholder="逗号分隔" /></label>
        <label>
          <span>状态</span>
          <select v-model="draft.status">
            <option value="draft">草稿</option>
            <option value="published">发布</option>
          </select>
        </label>
        <label class="full">
          <span>来源链接（仅后台）</span>
          <input v-model="draft.source_url" placeholder="运营溯源，前台不展示" />
        </label>
      </div>
      <label class="block-label">正文</label>
      <RichTextEditor
        v-model="draft.body_html"
        placeholder="可从公众号/网页粘贴 HTML，或直接编辑…"
        min-height="240px"
        allow-html-paste
      />

      <label class="block-label">关联算力卡</label>
      <div class="card-pick">
        <input v-model="cardQuery" placeholder="搜索卡名加入关联" @input="searchCards" />
        <ul v-if="cardHints.length" class="hints">
          <li v-for="card in cardHints" :key="card.id">
            <button type="button" @click="addCard(card)">{{ card.name }}</button>
          </li>
        </ul>
        <div class="picked">
          <span v-for="card in draft.cards" :key="card.id" class="chip">
            {{ card.name }}
            <button type="button" @click="removeCard(card.id)">×</button>
          </span>
          <span v-if="!draft.cards.length" class="muted">未关联时，文末不显示「查询这张卡参数」。</span>
        </div>
      </div>

      <div class="actions">
        <button type="button" class="btn primary" :disabled="saving" @click="save">
          {{ saving ? '保存中…' : '保存' }}
        </button>
        <button type="button" class="btn ghost" @click="cancelEdit">取消</button>
        <a
          v-if="draft.slug && draft.status === 'published'"
          class="btn ghost sm"
          :href="`/articles/${encodeURIComponent(draft.slug)}`"
          target="_blank"
          rel="noopener"
        >前台查看</a>
        <button v-if="draft.body_html" type="button" class="btn ghost sm" @click="previewOpen = !previewOpen">
          {{ previewOpen ? '收起预览' : '预览正文' }}
        </button>
      </div>
      <div v-if="previewOpen" class="preview-box" v-html="draft.body_html" />
    </div>

    <!-- 列表 -->
    <div v-else>
      <div class="search-row">
        <input v-model="keyword" placeholder="搜索标题或标签" @keyup.enter="load" />
        <div class="status-chips">
          <button type="button" :class="{ on: !statusFilter }" @click="setStatus('')">全部</button>
          <button type="button" :class="{ on: statusFilter === 'draft' }" @click="setStatus('draft')">草稿</button>
          <button type="button" :class="{ on: statusFilter === 'published' }" @click="setStatus('published')">已发布</button>
        </div>
        <button type="button" class="btn ghost sm" @click="load">搜索</button>
      </div>

      <table v-if="list.length" class="list">
        <thead>
          <tr>
            <th>标题</th>
            <th>状态</th>
            <th>标签</th>
            <th>时间</th>
            <th />
          </tr>
        </thead>
        <tbody>
          <tr v-for="item in list" :key="item.id">
            <td>
              <strong>{{ item.title }}</strong>
              <div class="muted">/articles/{{ item.slug }}</div>
              <div v-if="item.source_url" class="muted src">源：{{ item.source_url }}</div>
            </td>
            <td>
              <span :class="['tag', item.status === 'published' ? 'on' : 'off']">
                {{ item.status === 'published' ? '已发布' : '草稿' }}
              </span>
            </td>
            <td>{{ item.tags || '—' }}</td>
            <td>{{ formatTime(item.published_at || item.updated_at) || '—' }}</td>
            <td class="ops">
              <button type="button" class="btn ghost sm" @click="openEdit(item.id)">编辑</button>
              <a
                v-if="item.status === 'published'"
                class="btn ghost sm"
                :href="`/articles/${encodeURIComponent(item.slug)}`"
                target="_blank"
                rel="noopener"
              >查看</a>
              <button type="button" class="btn danger sm" @click="remove(item)">删除</button>
            </td>
          </tr>
        </tbody>
      </table>
      <p v-else class="muted">
        暂无文章。
        <button type="button" class="linkish" @click="startCreate">写第一篇</button>
        或
        <button type="button" class="linkish" @click="mode = 'import'">从链接导入</button>
      </p>
    </div>
  </section>
</template>

<script>
import RichTextEditor from '../../components/RichTextEditor.vue'
import { adminListCards } from '../../api/accelService'
import {
  adminCreateArticle,
  adminDeleteArticle,
  adminGetArticle,
  adminImportArticle,
  adminImportBatch,
  adminImportPreview,
  adminListArticles,
  adminUpdateArticle
} from '../../api/platformService'

const empty = () => ({
  id: null,
  title: '',
  slug: '',
  summary: '',
  body_html: '',
  tags: '',
  seo_keywords: '',
  source_url: '',
  status: 'draft',
  cards: []
})

export default {
  name: 'AdminArticles',
  components: { RichTextEditor },
  data() {
    return {
      mode: 'list',
      list: [],
      draft: empty(),
      editing: false,
      keyword: '',
      statusFilter: '',
      cardQuery: '',
      cardHints: [],
      error: '',
      message: '',
      saving: false,
      searchTimer: null,
      previewOpen: false,
      importUrl: '',
      importHtml: '',
      batchUrls: '',
      importing: false,
      importPreview: null,
      batchResults: [],
      pendingCards: []
    }
  },
  mounted() {
    this.load()
  },
  beforeUnmount() {
    if (this.searchTimer) clearTimeout(this.searchTimer)
  },
  methods: {
    formatTime(iso) {
      if (!iso) return ''
      return String(iso).replace('T', ' ').slice(0, 16)
    },
    setStatus(v) {
      this.statusFilter = v
      this.load()
    },
    async load() {
      this.error = ''
      try {
        const data = await adminListArticles({
          q: this.keyword.trim() || undefined,
          status: this.statusFilter || undefined
        })
        this.list = data.articles || []
      } catch (err) {
        this.error = err.response?.data?.error || '加载失败'
      }
    },
    startCreate() {
      this.editing = true
      this.draft = empty()
      this.message = ''
      this.error = ''
      this.previewOpen = false
      this.mode = 'edit'
    },
    cancelEdit() {
      this.editing = false
      this.draft = empty()
      this.previewOpen = false
      this.mode = 'list'
    },
    async openEdit(id) {
      this.error = ''
      this.message = ''
      try {
        const data = await adminGetArticle(id)
        this.draft = {
          id: data.id,
          title: data.title || '',
          slug: data.slug || '',
          summary: data.summary || '',
          body_html: data.body_html || '',
          tags: data.tags || '',
          seo_keywords: data.seo_keywords || '',
          source_url: data.source_url || '',
          status: data.status || 'draft',
          cards: data.cards || []
        }
        this.editing = true
        this.mode = 'edit'
        this.previewOpen = false
      } catch (err) {
        this.error = err.response?.data?.error || '加载失败'
      }
    },
    searchCards() {
      if (this.searchTimer) clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(async () => {
        const q = this.cardQuery.trim()
        if (!q) {
          this.cardHints = []
          return
        }
        try {
          const data = await adminListCards({ q })
          const rows = Array.isArray(data) ? data : (data.cards || [])
          const picked = new Set(this.draft.cards.map((c) => c.id))
          this.cardHints = rows.filter((c) => c && c.id && !picked.has(c.id)).slice(0, 8)
        } catch (e) {
          this.cardHints = []
        }
      }, 250)
    },
    addCard(card) {
      if (this.draft.cards.some((c) => c.id === card.id)) return
      this.draft.cards.push({ id: card.id, name: card.name, vendor: card.vendor || '' })
      this.cardQuery = ''
      this.cardHints = []
    },
    addSuggested(card) {
      if (!this.editing) {
        this.pendingCards.push(card)
        this.message = `已暂存关联「${card.name}」，填入编辑器后生效`
        return
      }
      this.addCard(card)
    },
    removeCard(id) {
      this.draft.cards = this.draft.cards.filter((c) => c.id !== id)
    },
    async doPreview() {
      this.error = ''
      this.message = ''
      this.importing = true
      this.importPreview = null
      try {
        const payload = this.importHtml.trim()
          ? { html: this.importHtml, url: this.importUrl.trim() || undefined }
          : { url: this.importUrl.trim() }
        this.importPreview = await adminImportPreview(payload)
        this.message = '预览已生成'
      } catch (err) {
        this.error = err.response?.data?.error || '预览失败'
      } finally {
        this.importing = false
      }
    },
    async doImportOne() {
      this.error = ''
      this.message = ''
      this.importing = true
      try {
        const payload = this.importHtml.trim()
          ? { html: this.importHtml, url: this.importUrl.trim() || undefined, save: true }
          : { url: this.importUrl.trim(), save: true }
        const data = await adminImportArticle(payload)
        this.message = `已导入草稿 #${data.article?.id}`
        this.importPreview = data.parsed || null
        await this.load()
        if (data.article?.id) await this.openEdit(data.article.id)
      } catch (err) {
        this.error = err.response?.data?.error || '导入失败'
      } finally {
        this.importing = false
      }
    },
    async doBatch() {
      this.error = ''
      this.message = ''
      this.importing = true
      this.batchResults = []
      try {
        const data = await adminImportBatch({ text: this.batchUrls })
        this.batchResults = data.results || []
        this.message = `批量完成：成功 ${data.ok || 0}，失败 ${data.fail || 0}`
        await this.load()
      } catch (err) {
        this.error = err.response?.data?.error || '批量导入失败'
      } finally {
        this.importing = false
      }
    },
    usePreviewIntoEditor() {
      if (!this.importPreview) return
      const suggested = this.importPreview.suggested_cards || []
      const pending = this.pendingCards || []
      const merged = []
      const seen = new Set()
      for (const c of [...suggested, ...pending]) {
        if (!c?.id || seen.has(c.id)) continue
        seen.add(c.id)
        merged.push({ id: c.id, name: c.name, vendor: c.vendor || '' })
      }
      this.draft = {
        ...empty(),
        title: this.importPreview.title || '',
        summary: this.importPreview.summary || '',
        body_html: this.importPreview.body_html || '',
        source_url: this.importPreview.source_url || this.importUrl || '',
        cards: merged
      }
      this.pendingCards = []
      this.editing = true
      this.mode = 'edit'
      this.previewOpen = true
      this.message = '已填入编辑器，请检查后保存'
    },
    async save() {
      this.error = ''
      this.message = ''
      this.saving = true
      const payload = {
        title: this.draft.title,
        slug: this.draft.slug,
        summary: this.draft.summary,
        body_html: this.draft.body_html,
        tags: this.draft.tags,
        seo_keywords: this.draft.seo_keywords,
        source_url: this.draft.source_url,
        status: this.draft.status,
        card_ids: this.draft.cards.map((c) => c.id)
      }
      try {
        const saved = this.draft.id
          ? await adminUpdateArticle(this.draft.id, payload)
          : await adminCreateArticle(payload)
        this.message = this.draft.status === 'published' ? '已保存并发布' : '已保存'
        this.draft = {
          ...this.draft,
          id: saved.id,
          slug: saved.slug,
          status: saved.status,
          source_url: saved.source_url || this.draft.source_url,
          cards: saved.cards || []
        }
        await this.load()
      } catch (err) {
        this.error = err.response?.data?.error || '保存失败'
      } finally {
        this.saving = false
      }
    },
    async remove(item) {
      if (!window.confirm(`删除「${item.title}」？`)) return
      try {
        await adminDeleteArticle(item.id)
        if (this.draft.id === item.id) this.cancelEdit()
        await this.load()
        this.message = '已删除'
      } catch (err) {
        this.error = err.response?.data?.error || '删除失败'
      }
    }
  }
}
</script>

<style scoped>
.mode-tabs {
  display: flex; gap: 6px; flex-wrap: wrap; margin: 12px 0 14px;
}
.mode-tabs button {
  border: 1px solid var(--line, #e5e6eb); background: #fff; border-radius: 8px;
  padding: 6px 12px; font: inherit; font-size: 13px; cursor: pointer; color: #4e5969;
}
.mode-tabs button.on {
  background: #e8f3ff; border-color: #bedaff; color: #165dff; font-weight: 600;
}
.sub-panel {
  margin-bottom: 16px; padding: 14px; border: 1px solid var(--line, #e5e6eb);
  border-radius: 12px; background: #fafbfc;
}
.form-grid {
  display: grid; grid-template-columns: 1fr 1fr; gap: 10px; margin-bottom: 12px;
}
.form-grid label { display: grid; gap: 4px; font-size: 13px; }
.form-grid .full { grid-column: 1 / -1; }
.form-grid input, .form-grid textarea, .form-grid select,
.search-row input, .card-pick input, .batch-box {
  border: 1px solid #e5e6eb; border-radius: 10px; padding: 8px 10px; font: inherit;
}
.batch-box { width: 100%; box-sizing: border-box; margin-bottom: 8px; }
.block-label { display: block; margin: 10px 0 6px; font-size: 13px; font-weight: 600; }
.search-row { display: flex; gap: 8px; margin-bottom: 14px; flex-wrap: wrap; align-items: center; }
.status-chips { display: flex; gap: 4px; }
.status-chips button {
  border: 1px solid #e5e6eb; background: #fff; border-radius: 999px; padding: 4px 10px;
  font: inherit; font-size: 12px; cursor: pointer; color: #4e5969;
}
.status-chips button.on { background: #e8f3ff; border-color: #bedaff; color: #165dff; }
.card-pick { position: relative; }
.hints {
  list-style: none; margin: 6px 0 0; padding: 6px; background: #fff; border: 1px solid #e5e6eb;
  border-radius: 12px; max-height: 180px; overflow: auto;
}
.hints button {
  width: 100%; text-align: left; border: 0; background: transparent; padding: 6px 8px;
  border-radius: 8px; cursor: pointer;
}
.hints button:hover { background: #e8f3ff; color: #165dff; }
.picked { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 8px; align-items: center; }
.chip {
  display: inline-flex; align-items: center; gap: 4px; background: #e8f3ff; color: #165dff;
  border-radius: 999px; padding: 2px 8px; font-size: 12px; border: 0; cursor: pointer; font: inherit;
}
.chip button { border: 0; background: transparent; cursor: pointer; color: inherit; }
.list { width: 100%; border-collapse: collapse; font-size: 13px; }
.list th, .list td { border-top: 1px solid #e5e6eb; padding: 8px 6px; text-align: left; vertical-align: top; }
.ops { white-space: nowrap; display: flex; gap: 4px; flex-wrap: wrap; }
.tag { font-size: 12px; padding: 2px 8px; border-radius: 999px; background: #f2f3f5; }
.tag.on { background: #e8ffea; color: #188038; }
.tag.off { background: #f2f3f5; color: #86909c; }
.actions { display: flex; gap: 8px; flex-wrap: wrap; margin-top: 12px; align-items: center; }
.preview-box {
  margin-top: 12px; padding: 14px; background: #fff; border: 1px solid #e5e6eb; border-radius: 12px;
  line-height: 1.75; max-height: 360px; overflow: auto;
}
.preview-box :deep(img) { max-width: 100%; height: auto; }
.import-preview { margin-top: 14px; padding-top: 14px; border-top: 1px solid #e5e6eb; }
.preview-head { display: flex; justify-content: space-between; gap: 12px; align-items: flex-start; }
.preview-head h3 { margin: 0 0 6px; font-size: 16px; }
.pill {
  flex-shrink: 0; font-size: 11px; padding: 2px 8px; border-radius: 999px;
  background: #e8f3ff; color: #165dff; border: 1px solid #bedaff;
}
.batch-results { list-style: none; margin: 10px 0 0; padding: 0; font-size: 13px; }
.batch-results li { padding: 4px 0; }
.batch-results .ok { color: #188038; }
.batch-results .bad { color: #d3382c; }
.linkish {
  border: 0; background: transparent; color: #165dff; cursor: pointer; font: inherit; padding: 0; margin-left: 6px;
}
.linkish:hover { text-decoration: underline; }
.src { font-size: 11px; word-break: break-all; }
@media (max-width: 800px) {
  .form-grid { grid-template-columns: 1fr; }
}
</style>
